import { cloneDeep, every, get, groupBy } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  formatNumber,
  salDeclarationCheckValues,
  unformatNumber,
  checkRole,
  showError,
  showSuccess,
} from "../../../utils/utilities";
import "./SalDeclaration.css";
import { Col, Row, Typography, Table, Input, Modal, Space, Alert, Tooltip } from "antd";
import {
  SaveOutlined,
  CheckOutlined,
  RiseOutlined,
  RollbackOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import OrderProposal from "../../../services/api/OrderProposal";
import { setLoading } from "../../../redux/ducks/Loading";
import { setOrder } from "../../../redux/ducks/Orders";
const { Title, Text } = Typography;
const { confirm } = Modal;

const WorkMonth = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation();
  const order = useSelector((state) => get(state.OrdersDuck, "order", {}));
  const withValueLimit = ({ floatValue }) => floatValue <= order.residualValue;
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const [state, setState] = useState({
    isAdmin: userInfo.roles ? checkRole(userInfo.roles, "SYSTEM_IT") : false,
    dataContent: [],
    loading: false,
    supportPm: [],
    canConfirm: false,
    showErrorMessage: false,
    errorMessage: "",
    allApprovedOrWaiting: false,
    selectedForApprovalKeys: [],
    isDirty: false,
    modal: {
      visible: false,
    },
  });
  useEffect(() => {
    dataGet();
  }, [id]);
  const openModal = () => {
    setState((prevState) => ({ ...prevState, modal: { visible: true } }));
  };
  const closeModal = () => {
    setState((prevState) => ({ ...prevState, modal: { visible: false } }));
  };
  const refreshContext = () => {
    dataGet();
    refreshOrder();
  };
  const refreshOrder = async () => {
    dispatch(setLoading(true));
    const order = await OrderProposal.getOrder(id);
    if (!order.error) {
      dispatch(setOrder(get(order, "data", {})));
    } else {
      showError(get(order, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };
  const dataGet = async () => {
    dispatch(setLoading(true));
    const tasks = await OrderProposal.listTasks(id);
    if (!tasks.error) {
      let canConfirm = false;
      const groupedTasks = groupBy(tasks.data, (t) => t.projectTaskId);
      let index = 0;
      // eslint-disable-next-line no-unused-vars
      Object.entries(groupedTasks).forEach(([_, tasks]) => {
        let showPlusButton = true;
        tasks.forEach((t, i) => {
          if (t.status === "PLANNING") {
            canConfirm = true;
          }

          if (["CREATED", "PLANNING", "REFUTED"].includes(t.status)) {
            if (tasks.length > 1) {
              t.showDeleteButton = true;
            }

            if (i !== tasks.length - 1) {
              // if I find a CREATED, PLANNING or REFUTED record, I won't show the plus button for this projectTask
              showPlusButton = false;
            }
          } else if (i === tasks.length - 1) {
            // if I get to the last record and it is approved or waiting approval, I will show the plus button for this projectTask only if all other records of this projectTask were approved or waiting approval
            // (if the last record is created, planning or refuted, I don't enter here and I don't show the plus button)
            t.showPlusButton = showPlusButton;
          }
          t.key = index++;
        });
      });
      setState((prevState) => ({
        ...prevState,
        dataContent: Object.values(groupedTasks).flat(),
        canConfirm,
        allApprovedOrWaiting: every(tasks.data, ["status", "APPROVED" || "WAITING_APPROVAL_1" || "WAITING_APPROVAL_2"]),
      }));
    } else {
      showError(get(tasks, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };
  const changeInput = (e, name, record) => {
    let errorMessage = "";
    const currentData = cloneDeep(state.dataContent);
    currentData[record.key][name] =
      name === "amount" ? unformatNumber(e.target.value, currentLanguage) : e.target.value;
    const check = salDeclarationCheckValues(currentData, order.residualValue);
    errorMessage = !check ? "Limite superato" : "";
    setState((prevState) => ({
      ...prevState,
      dataContent: currentData,
      errorMessage,
      showErrorMessage: !check,
      isDirty: true,
    }));
  };
  const blurInput = () => {
    if (state.errorMessage !== "") {
      setState((prevState) => ({ ...prevState, showErrorMessage: true }));
    }
  };
  const onSubmit = async (status) => {
    const dataSave = [];
    setState((prevState) => ({ ...prevState, loading: true }));
    const content =
      status === "WAITING_APPROVAL_1"
        ? state.dataContent.filter((dc, i) => state.selectedForApprovalKeys.includes(i))
        : state.dataContent;
    content.forEach((dc) => {
      if (dc.status !== "APPROVED" && dc.status !== "WAITING_APPROVAL_1" && dc.status !== "WAITING_APPROVAL_2") {
        if (state.isAdmin || (!state.isAdmin && dc.enabled)) {
          dataSave.push({
            id: dc.id,
            amount: dc.amount,
            noteSupplier: dc.noteSupplier,
            orderId: dc.orderId,
            projectTaskId: dc.projectTaskId,
            status,
          });
        }
      }
    });
    const submit = await OrderProposal.saveOrder(dataSave);
    if (!submit.error) {
      showSuccess();
      refreshContext();
    } else {
      showError(get(submit, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({
      ...prevState,
      loading: false,
      isDirty: false,
      selectedForApprovalKeys: [],
    }));
    closeModal();
  };
  const rollbackCallback = (id) => () => {
    confirm({
      title: t("salDeclaration.workMonth.rollBackAlertMessage"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        setState((prevState) => ({ ...prevState, loading: true }));
        const rollBack = await OrderProposal.waitingApprovalRollback(id);
        if (!rollBack.error) {
          showSuccess();
          dataGet();
        } else {
          showError(get(rollBack, "error.messageCode", "ER000"));
        }
        setState((prevState) => ({ ...prevState, loading: false }));
      },
      onCancel() {},
    });
  };
  const addProjectTaskOrder = async (record) => {
    const add = await OrderProposal.addProjectTaskOrder(record.orderId, record.projectTaskId);
    if (!add.error) {
      showSuccess();
      dataGet();
    } else {
      showError(get(add, "error.messageCode", "ER000"));
    }
  };
  const deleteProjectTaskOrder = async (id) => {
    const response = await OrderProposal.deleteProjectTaskOrder(id);
    if (!response.error) {
      showSuccess();
      dataGet();
    } else {
      showError(get(response, "error.messageCode", "ER000"));
    }
  };
  const columns = [
    {
      title: t("salDeclaration.workMonth.project"),
      dataIndex: "projectName",
      key: "projectName",
      render: (projectName, record) => `${record.projectId} | ${projectName}`,
    },
    {
      title: t("salDeclaration.workMonth.projectActivity"),
      dataIndex: "projectTaskName",
      key: "projectTaskName",
      render: (projectTaskName, record) => `${record.projectTaskId} | ${projectTaskName}`,
    },
    {
      title: t("salDeclaration.workMonth.finalBalance"),
      dataIndex: "amount",
      key: "amount",
      render: (amount, record) =>
        formatNumber({
          name: `amount_${record.id}`,
          value: amount,
          lang: currentLanguage,
          id: `amount_${record.id}`,
          key: record.id,
          displayType: "input",
          suffix: "EUR",
          customInput: Input,
          onChange: (e) => changeInput(e, "amount", record),
          onBlur: blurInput,
          isAllowed: withValueLimit,
          disabled:
            ["APPROVED", "WAITING_APPROVAL_1", "WAITING_APPROVAL_2"].includes(record.status) ||
            (!state.isAdmin && order.statusOrder === "BLOCKED") ||
            (!state.isAdmin && !record.enabled),
        }),
    },
    {
      title: t("salDeclaration.workMonth.status"),
      dataIndex: "status",
      key: "status",
      width: 135,
      render: (status, record) => {
        let color;
        let text = "";
        switch (status) {
          case "REFUTED":
            color = "status-refuted";
            text = t("salDeclaration.workMonth.refuted");
            break;
          case "APPROVED":
            color = "status-approved";
            text = t("salDeclaration.workMonth.approved");
            break;
          case "WAITING_APPROVAL_1":
            color = "status-waiting";
            text = t("salDeclaration.workMonth.waiting_approval_1");
            break;
          case "WAITING_APPROVAL_2":
            color = "status-waiting-2";
            text = t("salDeclaration.workMonth.waiting_approval_2");
            break;
          case "PLANNING":
            color = "status-draft";
            text = t("salDeclaration.workMonth.draft");
            break;
          case "CREATED":
          default:
            color = "";
            text = "";
            break;
        }
        return (
          <div key={record.id} className={color}>
            {text}
          </div>
        );
      },
    },
    {
      title: t("salDeclaration.workMonth.supplierNotes"),
      dataIndex: "noteSupplier",
      key: "noteSupplier",
      render: (noteSupplier, record) => (
        <Tooltip title={noteSupplier}>
          <div>
            <Input
              id={`noteSuppliers_${record.id}`}
              name={`noteSuppliers_${record.id}`}
              key={record.id}
              value={noteSupplier}
              onChange={(e) => changeInput(e, "noteSupplier", record)}
              disabled={
                ["APPROVED", "WAITING_APPROVAL_1", "WAITING_APPROVAL_2"].includes(record.status) ||
                (!state.isAdmin && order.statusOrder === "BLOCKED") ||
                (!state.isAdmin && !record.enabled)
              }
            />
          </div>
        </Tooltip>
      ),
    },
    {
      title: t("salDeclaration.workMonth.functionManagerNotes"),
      dataIndex: "noteTimesheetApprover",
      key: "noteTimesheetApprover",
      render: (noteTimesheetApprover) => <Tooltip title={noteTimesheetApprover}>{noteTimesheetApprover}</Tooltip>,
    },
    {
      title: t("salDeclaration.workMonth.pmNotes"),
      dataIndex: "notePm",
      key: "notePm",
      render: (notePm) => <Tooltip title={notePm}>{notePm}</Tooltip>,
    },
    {
      title: "",
      width: 60,
      render: (_, data) => {
        return (
          <Row align="middle" justify="space-between" gutter={[8, 4]}>
            {state.isAdmin && data.status === "APPROVED" && (
              <Button
                currentSize="small"
                isLoading={state.loading}
                clickCallback={rollbackCallback(data.id)}
                currentIcon={<RollbackOutlined className="icon-small" />}
              />
            )}
            {data.showPlusButton && (
              <Button
                currentSize="small"
                clickCallback={() => addProjectTaskOrder(data)}
                currentIcon={<PlusOutlined className="icon-small" />}
                style={{ padding: "4px 0px" }}
              />
            )}
            {data.showDeleteButton && (state.isAdmin || checkRole(userInfo.roles, "SAL_SUBSTITUTE")) && (
              <Button
                currentSize="small"
                clickCallback={() => deleteProjectTaskOrder(data.id)}
                currentIcon={<DeleteOutlined className="icon-small" />}
                style={{ padding: "4px 0px" }}
              />
            )}
          </Row>
        );
      },
    },
  ];
  return (
    <>
      <Alert message={state.errorMessage} type="error" className={`mb-10 ${!state.showErrorMessage ? "d-none" : ""}`} />
      <Row className="mb-10">
        <Col lg={8} md={24}>
          <Row>
            <Col>
              <Text className="mr-10px">{t("salDeclaration.workMonth.residualValue")}</Text>
            </Col>
            <Col className="right">
              <div className="boxValue">
                {formatNumber({
                  value: order.residualValue,
                  lang: currentLanguage,
                  suffix: "EUR",
                })}
              </div>
            </Col>
          </Row>
        </Col>
        <Col lg={8} md={24}>
          <Row>
            <Col>
              <Text className="mr-10px">{t("salDeclaration.workMonth.monthDeclared")}</Text>
            </Col>
            <Col className="right">
              <div className="boxValue">
                {formatNumber({
                  value: order.monthValue,
                  lang: currentLanguage,
                  suffix: "EUR",
                })}
              </div>
            </Col>
          </Row>
        </Col>
        <Col lg={8} md={24}>
          <Row>
            <Col>
              <Text className="mr-10px">{t("salDeclaration.workMonth.finalValue")}</Text>
            </Col>
            <Col className="right">
              <div className="boxValue">
                {formatNumber({
                  value: order.residualTotal,
                  lang: currentLanguage,
                  suffix: "EUR",
                })}
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      <Table
        dataSource={state.dataContent}
        columns={columns}
        size="small"
        pagination={false}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: state.selectedForApprovalKeys,
          onChange: (selectedRowKeys) => {
            setState((prevState) => ({
              ...prevState,
              selectedForApprovalKeys: selectedRowKeys,
            }));
          },
          getCheckboxProps: (record) => ({
            disabled: record.status !== "PLANNING",
          }),
          columnTitle: (selectAllCheckbox) => {
            return (
              <Row align="middle" gutter={[8, 8]}>
                <Col span={4}>{selectAllCheckbox}</Col>
                <Col span={20}>
                  <Text>{t("salDeclaration.workMonth.selectForApproval")}</Text>
                </Col>
              </Row>
            );
          },
          columnWidth: 130,
        }}
      />
      <Row type="flex" align="middle" justify="end" className="mt-10" gutter={[16, 8]}>
        <Col>
          <Button
            currentSize="large"
            isLoading={state.loading}
            isDisabled={
              state.allApprovedOrWaiting ||
              !state.isDirty ||
              state.showErrorMessage ||
              (!state.isAdmin && order.statusOrder === "BLOCKED")
            }
            clickCallback={() => onSubmit("PLANNING")}
            currentIcon={<SaveOutlined className="icon-large" />}
          />
        </Col>
        <Col>
          <Button
            currentSize="large"
            isLoading={state.loading}
            isDisabled={
              !state.canConfirm ||
              !state.selectedForApprovalKeys.length ||
              state.showErrorMessage ||
              (!state.isAdmin && order.statusOrder === "BLOCKED")
            }
            clickCallback={openModal}
            currentIcon={<RiseOutlined className="icon-large" />}
          />
        </Col>
      </Row>
      <Modal
        open={state.modal.visible}
        width={700}
        title={t("salDeclaration.workMonth.modalTitle")}
        footer={[
          <Button
            key="okButton"
            currentIcon={<CheckOutlined className="icon-medium" />}
            clickCallback={() => onSubmit("WAITING_APPROVAL_1")}
          />,
        ]}
        onCancel={closeModal}
      >
        <Space direction="vertical">
          <Title level={5}>ODA {order.id}</Title>
        </Space>
        <Row>
          <Col span={12}>
            <Title level={5}>{t("salDeclaration.workMonth.projectActivity")}</Title>
          </Col>
          <Col span={4}>
            <Title level={5}>{t("salDeclaration.workMonth.finalBalance")}</Title>
          </Col>
          <Col span={8}>
            <Title level={5}>{t("salDeclaration.workMonth.supplierNotes")}</Title>
          </Col>
        </Row>
        {state.dataContent.map(
          (dc, i) =>
            state.selectedForApprovalKeys.includes(i) && (
              <Row key={i}>
                <Col span={12}>
                  <Text>{dc.projectName}</Text>
                </Col>
                <Col span={4}>
                  <Text>
                    {formatNumber({
                      value: dc.amount,
                      lang: currentLanguage,
                      suffix: "EUR",
                    })}
                  </Text>
                </Col>
                <Col span={8}>
                  <Text>{dc.noteSupplier}</Text>
                </Col>
              </Row>
            )
        )}
      </Modal>
    </>
  );
};

export default WorkMonth;
